import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'
import { DateField } from '@/ui/form/controls/DateField'
import { TimeField } from '@/ui/form/controls/TimeField'
import { Checkbox } from '@/ui/form/primitives/checkbox'
import { toast } from '@/lib/toasts/api'
import { FaIcon, faPlus } from '@/lib/fa'
import { faArrowLeft, faTrash } from '@fortawesome/free-solid-svg-icons'
import { useQueryCountries } from '@/modules/shared/query-client/useQueryCountries'
import DividerVertical from '@/modules/drivers/shared/DividerVertical'
import { createBorderEntries } from './borderEntries'
import type { BorderEntry } from './borderEntries'

interface BordersViewProps {
  companyId: string
  driverId: number
  dateFrom: Moment
  dateTo: Moment
  onClose: () => void
  onSave?: (events: EventModel[]) => void
  translate: (key: string) => string
}

export const BordersView: React.FC<BordersViewProps> = ({
  companyId,
  driverId,
  dateFrom: initialDateFrom,
  dateTo: initialDateTo,
  onClose,
  onSave,
  translate,
}) => {
  const { getGpsFromTachograph, getGpsData } = useBusinessTripService()
  const { data: countries = [] } = useQueryCountries({ companyId })

  const [data, setData] = React.useState<BorderEntry[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [dateFrom, setDateFrom] = React.useState<string>(
    initialDateFrom.format('YYYY-MM-DD'),
  )
  const [dateTo, setDateTo] = React.useState<string>(
    initialDateTo.format('YYYY-MM-DD'),
  )
  const [showArrival, setShowArrival] = React.useState(true)
  const [showDeparture, setShowDeparture] = React.useState(true)

  React.useEffect(() => {
    let cancelled = false
    const fetchData = async () => {
      if (!driverId || !dateFrom || !dateTo) {
        setData([])
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        const params = {
          companyId,
          driverId: String(driverId),
          startTime: dateFrom,
          endTime: dateTo,
        }
        const [tachographData, gpsData] = await Promise.all([
          getGpsFromTachograph(params),
          getGpsData(params),
        ])
        if (cancelled) return
        const entries = createBorderEntries({
          tachographData: tachographData || [],
          gpsData: gpsData || [],
          dateFrom,
          dateTo,
        })
        setData((prev) => [...entries, ...prev.filter((item) => item.isManual)])
      } catch (err: any) {
        if (cancelled) return
        console.error('Error fetching border crossing data:', err)
        if (err?.response) {
          toast.error({
            text:
              err?.response?.data?.message ||
              err?.message ||
              translate('errorCodes.retrieving-borders-failed'),
          })
        }
        setData([])
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchData()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, driverId, dateFrom, dateTo])

  const filteredData = data.filter((item) => {
    if (item.isManual) return true
    if (!showArrival && item.isArrival) return false
    if (!showDeparture && !item.isArrival) return false
    return true
  })

  const handleToggleAll = (checked: boolean) => {
    const filteredIds = new Set(filteredData.map((item) => item.id))
    setData((prev) =>
      prev.map((item) =>
        filteredIds.has(item.id) ? { ...item, checked } : item,
      ),
    )
  }

  const handleToggleItem = (
    id: string | undefined,
    index: number,
    checked: boolean,
  ) => {
    setData((prev) =>
      prev.map((item, i) =>
        (id != null ? item.id === id : i === index)
          ? { ...item, checked }
          : item,
      ),
    )
  }

  const handleToggleTransit = (id: string, isTransit: boolean) => {
    setData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, isTransit } : item)),
    )
  }

  const handleToggleAllTransit = (isTransit: boolean) => {
    const filteredIds = new Set(filteredData.map((item) => item.id))
    setData((prev) =>
      prev.map((item) =>
        filteredIds.has(item.id) ? { ...item, isTransit } : item,
      ),
    )
  }

  const handleAdd = () => {
    setData((prev) => [
      ...prev,
      {
        id: `manual_${Date.now()}_${Math.random()}`,
        date: dateTo ? moment(dateTo) : moment(),
        country: null as any,
        isArrival: true,
        isTransit: false,
        isHidden: false,
        checked: true,
        isManual: true,
        city: '',
      },
    ])
  }

  const handleUpdateManual = (id: string, updates: Partial<BorderEntry>) => {
    setData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item)),
    )
  }

  const handleDeleteManual = (id: string) => {
    setData((prev) => prev.filter((item) => item.id !== id))
  }

  const handleSave = () => {
    if (!onSave) return

    const selectedItems = data.filter((item) => item.checked)
    if (selectedItems.length === 0) {
      toast.error({ text: translate('newDelegation.no-items-selected') })
      return
    }

    const events: EventModel[] = selectedItems
      .filter((item) => item.date && item.country)
      .map((item, i) => ({
        date: moment(item.date),
        tripEvent: TripEventType.ChangeCountry,
        country: item.country,
        city: item.city || '',
        isTransit: item.isTransit || false,
        lp: i,
      }))

    if (events.length === 0) {
      toast.warning({ text: translate('newDelegation.no-events-created') })
      return
    }

    onSave(events)
    toast.success({ text: translate('newDelegation.borders-imported') })
  }

  const allChecked =
    filteredData.length > 0 && filteredData.every((item) => item.checked)
  const allTransitChecked =
    filteredData.length > 0 &&
    filteredData.every((item) => item.isTransit)

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-bg-white">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-4 tw-border-b tw-bg-[#fafbff] tw-px-6 tw-py-4">
        <button
          onClick={onClose}
          className="tw-flex tw-items-center tw-gap-2 tw-rounded tw-p-2 hover:tw-bg-gray-100"
        >
          <FaIcon icon={faArrowLeft} className="tw-h-4 tw-w-4" />
        </button>
        <h2 className="tw-text-lg tw-font-semibold">
          {translate('businessTrip.borders')}
        </h2>
      </div>

      {/* Filters */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-6 tw-border-b tw-px-6 tw-py-4">
        <div className="tw-w-[180px] ">
          <DateField
            label={translate('newDelegation.dateFrom')}
            value={dateFrom}
            setValue={(val) => setDateFrom(val || '')}
            placeholder="DD-MM-RRRR"
          />
        </div>
        <div className="tw-w-[180px]">
          <DateField
            label={translate('newDelegation.dateTo')}
            value={dateTo}
            setValue={(val) => setDateTo(val || '')}
            placeholder="DD-MM-RRRR"
          />
        </div>
        <DividerVertical/>
        <div className="tw-flex tw-items-center tw-gap-2">
          <Checkbox
            id="borders-show-arrival"
            checked={showArrival}
            onChange={(e) => setShowArrival(e.target.checked)}
          />
          <label
            htmlFor="borders-show-arrival"
            className="tw-m-0 tw-p-0 tw-text-sm tw-text-content-base"
          >
            {translate('gpsFromTachograph.showCountryArrival')}
          </label>
        </div>
        <div className="tw-items-center tw-flex tw-gap-2">
          <Checkbox
            id="borders-show-departure"
            checked={showDeparture}
            onChange={(e) => setShowDeparture(e.target.checked)}
          />
          <label
            htmlFor="borders-show-departure"
            className="tw-m-0 tw-p-0 tw-text-sm tw-text-content-base"
          >
            {translate('gpsFromTachograph.showCountryLeave')}
          </label>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <Checkbox
            id="borders-check-all-transit"
            checked={allTransitChecked}
            onChange={(e) => handleToggleAllTransit(e.target.checked)}
          />
          <label
            htmlFor="borders-check-all-transit"
            className="tw-m-0 tw-p-0 tw-text-sm tw-text-content-base"
          >
            {translate('gpsFromTachograph.checkUncheckTransitRows')}
          </label>
        </div>
      </div>

      {/* Action bar */}
      <div className="tw-flex tw-w-full tw-items-center tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-3">
        {onSave && (
          <div
            className="tw-flex tw-h-5 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6]"
            onClick={handleAdd}
          >
            <FaIcon icon={faPlus} className="tw-h-4 tw-w-4 tw-text-[#2448bb]" />
            <span className="tw-text-sm tw-font-medium tw-text-[#2448bb]">
              {translate('common.add')}
            </span>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="tw-flex-1 tw-overflow-auto">
        {isLoading ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
            <span className="tw-text-content-secondary">
              {translate('gridPager.loadingText')}
            </span>
          </div>
        ) : (
          <table className="tw-w-full tw-table-auto">
            <thead className="tw-sticky tw-top-0 tw-z-10">
              <tr className="tw-border-b tw-bg-[#f2f2f2]">
                <th className="tw-w-10 tw-p-2">
                  <Checkbox
                    checked={allChecked}
                    onChange={(e) => handleToggleAll(e.target.checked)}
                  />
                </th>
                <th className="tw-p-2 tw-text-left tw-text-sm tw-font-medium tw-uppercase tw-text-content-base">
                  {translate('common.date')}
                </th>
                <th className="tw-p-2 tw-text-left tw-text-sm tw-font-medium tw-uppercase tw-text-content-base">
                  {translate('common.time')}
                </th>
                <th className="tw-p-2 tw-text-left tw-text-sm tw-font-medium tw-uppercase tw-text-content-base">
                  {translate('common.country')}
                </th>
                <th className="tw-p-2 tw-text-left tw-text-sm tw-font-medium tw-uppercase tw-text-content-base">
                  {translate('common.city')}
                </th>
                <th className="tw-p-2 tw-text-left tw-text-sm tw-font-medium tw-uppercase tw-text-content-base">
                  {translate('businessTrip.country-entry')}
                </th>
                <th className="tw-p-2 tw-text-left tw-text-sm tw-font-medium tw-uppercase tw-text-content-base">
                  {translate('businessTrip.excluded-from-delegation')}
                </th>
                <th className="tw-p-2" />
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={8} className="tw-p-8 tw-text-center">
                    <p className="tw-text-content-base tw-opacity-70">
                      {translate('businessTrip.no-data')}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredData.map((item, index) =>
                  item.isManual ? (
                    <tr
                      key={item.id}
                      className="tw-border-b hover:tw-bg-gray-50"
                    >
                      <td
                        className="tw-p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={item.checked || false}
                          onChange={(e) =>
                            handleToggleItem(item.id, index, e.target.checked)
                          }
                        />
                      </td>
                      <td
                        className="tw-p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DateField
                          value={
                            item.date
                              ? moment(item.date).format('YYYY-MM-DD')
                              : ''
                          }
                          setValue={(val) => {
                            const currentTime = item.date
                              ? moment(item.date).format('HH:mm')
                              : '00:00'
                            handleUpdateManual(item.id!, {
                              date: val
                                ? moment(`${val} ${currentTime}`)
                                : undefined,
                            })
                          }}
                        />
                      </td>
                      <td
                        className="tw-p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TimeField
                          inputSize="sm"
                          value={
                            item.date ? moment(item.date).format('HH:mm') : ''
                          }
                          setValue={(val) => {
                            const currentDate = item.date
                              ? moment(item.date).format('YYYY-MM-DD')
                              : moment().format('YYYY-MM-DD')
                            handleUpdateManual(item.id!, {
                              date: moment(`${currentDate} ${val ?? '00:00'}`),
                            })
                          }}
                        />
                      </td>
                      <td
                        className="tw-w-[130px] tw-p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <select
                          value={item.country?.id?.toString() ?? ''}
                          onChange={(e) => {
                            const found = (countries as any[]).find(
                              (c) => c.id.toString() === e.target.value,
                            )
                            if (found)
                              handleUpdateManual(item.id!, { country: found })
                          }}
                          className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1 tw-text-sm focus:tw-outline-none"
                        >
                          <option value="">
                            {translate(
                              'newDelegation.accomodation.choose-country',
                            )}
                          </option>
                          {(countries as any[]).map((c) => (
                            <option key={c.id} value={c.id.toString()}>
                              {c.code
                                ? translate(`countries.${c.code}`)
                                : c.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td
                        className="tw-p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="text"
                          value={item.city ?? ''}
                          onChange={(e) =>
                            handleUpdateManual(item.id!, {
                              city: e.target.value,
                            })
                          }
                          className="tw-w-full tw-rounded tw-border tw-border-base-300 tw-px-2 tw-py-1 tw-text-sm focus:tw-outline-none"
                        />
                      </td>
                      <td
                        className="tw-p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="tw-flex tw-items-center tw-justify-center">
                          <Checkbox
                            id={`arrival_manual_${item.id}`}
                            checked={item.isArrival || false}
                            onChange={(e) =>
                              handleUpdateManual(item.id!, {
                                isArrival: e.target.checked,
                              })
                            }
                          />
                        </div>
                      </td>
                      <td
                        className="tw-p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="tw-flex tw-items-center tw-justify-center">
                          <Checkbox
                            id={`transit_manual_${item.id}`}
                            checked={item.isTransit || false}
                            onChange={(e) =>
                              handleToggleTransit(item.id!, e.target.checked)
                            }
                          />
                        </div>
                      </td>
                      <td
                        className="tw-p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => handleDeleteManual(item.id!)}
                          className="tw-flex tw-items-center tw-justify-center tw-rounded tw-p-1 tw-text-error hover:tw-bg-red-50"
                        >
                          <FaIcon
                            icon={faTrash}
                            className="tw-h-3.5 tw-w-3.5"
                          />
                        </button>
                      </td>
                    </tr>
                  ) : (
                    <tr
                      key={item.id || index}
                      className="tw-cursor-pointer tw-border-b hover:tw-bg-gray-50"
                      onClick={() =>
                        onSave &&
                        handleToggleItem(item.id, index, !item.checked)
                      }
                    >
                      <td
                        className="tw-p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={item.checked || false}
                          onChange={(e) =>
                            handleToggleItem(item.id, index, e.target.checked)
                          }
                        />
                      </td>
                      <td className="tw-p-2 tw-text-sm tw-text-content-base">
                        {item.date
                          ? moment(item.date).format('DD-MM-YYYY')
                          : '-'}
                      </td>
                      <td className="tw-p-2 tw-text-sm tw-text-content-base">
                        {item.date ? moment(item.date).format('HH:mm') : '-'}
                      </td>
                      <td className="tw-p-2 tw-text-sm tw-text-content-base">
                        {item.country?.code
                          ? translate(`countries.${item.country.code}`)
                          : item.country?.name || '-'}
                      </td>
                      <td className="tw-p-2 tw-text-sm tw-text-content-base">
                        {item.city || ''}
                      </td>
                      <td className="tw-p-2 tw-text-sm tw-text-content-base">
                        {item.isArrival
                          ? translate('common.yes')
                          : translate('common.no')}
                      </td>
                      <td
                        className="tw-p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="tw-flex tw-items-center tw-justify-center">
                          <Checkbox
                            id={`transit_${item.id}`}
                            checked={item.isTransit || false}
                            onChange={(e) =>
                              handleToggleTransit(item.id!, e.target.checked)
                            }
                          />
                        </div>
                      </td>
                      <td className="tw-p-2" />
                    </tr>
                  ),
                )
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="tw-flex tw-items-center tw-justify-end tw-gap-4 tw-border-t tw-px-6 tw-py-4">
        <button
          onClick={onClose}
          className="hover:tw-bg-base-50 tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base"
        >
          {translate('answer.cancel')}
        </button>
        {onSave && (
          <button
            onClick={handleSave}
            className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary/90"
          >
            {translate('businessTrip.add-to-events')}
          </button>
        )}
      </div>
    </div>
  )
}
